import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { supabase, isSupabaseConfigured } from '../services/supabaseClient';
import DeleteDataModal from './settings/DeleteDataModal';

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [investDropdownOpen, setInvestDropdownOpen] = useState(false);
  const [learnDropdownOpen, setLearnDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [hasInvestAccess, setHasInvestAccess] = useState(false);
  const [hasModelPortfolioAccess, setHasModelPortfolioAccess] = useState(false);
  const [hasETFPortfolioAccess, setHasETFPortfolioAccess] = useState(false);
  const [hasSentinelAccess, setHasSentinelAccess] = useState(false);
  const [mobile, setMobile] = useState('');
  const [mobileSaving, setMobileSaving] = useState(false);
  const [mobileSaved, setMobileSaved] = useState(false);

  const toolsRef = useRef(null);
  const investRef = useRef(null);
  const learnRef = useRef(null);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const { user, signOut, setShowAuthModal } = useAuth();
  const { hasFeature } = useSubscription();
  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setToolsDropdownOpen(false);
    setInvestDropdownOpen(false);
    setLearnDropdownOpen(false);
    setUserMenuOpen(false);
  }, [location]);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || !isSupabaseConfigured()) {
        setIsAdmin(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select(
            'is_admin, access_invest_now, access_model_portfolio, access_etf_portfolio, access_sentinel, mobile'
          )
          .eq('id', user.id)
          .single();
        if (!error && data) {
          setIsAdmin(data.is_admin || false);
          setHasInvestAccess(data.access_invest_now || false);
          setHasModelPortfolioAccess(data.access_model_portfolio || false);
          setHasETFPortfolioAccess(data.access_etf_portfolio || false);
          setHasSentinelAccess(data.access_sentinel || false);
          if (data.mobile) setMobile(data.mobile);
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      }
    };
    checkAdminStatus();
  }, [user]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toolsRef.current && !toolsRef.current.contains(event.target)) {
        setToolsDropdownOpen(false);
      }
      if (investRef.current && !investRef.current.contains(event.target)) {
        setInvestDropdownOpen(false);
      }
      if (learnRef.current && !learnRef.current.contains(event.target)) {
        setLearnDropdownOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        setToolsDropdownOpen(false);
        setInvestDropdownOpen(false);
        setLearnDropdownOpen(false);
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
  };

  const handleSaveMobile = async () => {
    if (!user || !isSupabaseConfigured()) return;
    const digits = mobile.replace(/\D/g, '');
    if (digits && digits.length !== 10) return;
    setMobileSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ mobile: digits || null })
      .eq('id', user.id);
    setMobileSaving(false);
    if (!error) {
      setMobileSaved(true);
      setTimeout(() => setMobileSaved(false), 2000);
    }
  };

  // PLAN YOUR FREEDOM (primary journey)
  const planItems = [
    {
      name: '📅 Annual Cash Flow Planner',
      url: '/tools/planner',
      description: 'Plan your year — income, expenses, surplus',
      badge: 'NEW',
    },
    {
      name: '📊 Personal Inflation Index',
      url: '/tools/inflation-index',
      description: 'Your real inflation rate — not the official CPI',
      badge: 'NEW',
    },
    {
      name: 'Financial Planning Suite',
      url: '/financial-health-check',
      description: 'Complete guided financial plan',
      featured: true,
    },
  ];

  // EXPLORE
  const exploreItems = [
    { name: '🛡️ Insurance Planner', url: '/tools/insurance', description: 'Assess your coverage' },
  ];

  // RETIREMENT IN INDIA: YOUR OPTIONS (research tools)
  const retirementTools = [
    {
      name: 'Comprehensive Calculator',
      url: '/tools/freedom-number',
      description: 'Detailed retirement analysis',
    },
    {
      name: '🎯 Corpus Optimizer',
      url: '/tools/corpus-optimizer',
      description: 'Reduce your target step-by-step',
    },
    {
      name: '💡 Optimize Your Plan',
      url: '/tools/optimized-retirement',
      description: '5.5% SWR simulation',
    },
    {
      name: 'Stress Test',
      url: '/tools/stress-test',
      description: 'Test against 1,000 market scenarios',
    },
    {
      name: '🏠 Reverse Mortgage',
      url: '/tools/reverse-mortgage',
      description: 'Unlock home equity income',
    },
    {
      name: 'Plan B (Relocation)',
      url: '/tools/relocation',
      description: 'Alternative city options',
    },
  ];

  // INVEST — split into 2 columns to avoid scrollbar
  const investSignals = [
    ...(isAdmin
      ? [
          {
            name: '💰 Where to Invest now?',
            url: '/invest',
            description: 'Smart recommendations based on market',
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            name: '⚡ Market Pulse',
            url: '/tools/market-pulse',
            description: 'AI signals from Indian financial news',
            badge: 'NEW',
          },
        ]
      : []),
    ...(isAdmin || hasSentinelAccess
      ? [
          {
            name: '🛰️ Sentinel',
            url: '/tools/sentinel',
            description: 'Structural trend signals across 12 asset classes',
            badge: 'NEW',
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            name: '🎯 Sentinel Leaders',
            url: '/admin/sentinel-leaders',
            description: 'Layer 2 sector leadership rankings (admin research view)',
            badge: 'NEW',
          },
        ]
      : []),
    ...(hasModelPortfolioAccess
      ? [
          {
            name: '📐 Model Portfolio',
            url: '/tools/model-portfolio',
            description: 'Quant-screened India + US picks',
            badge: 'NEW',
          },
        ]
      : []),
    ...(hasETFPortfolioAccess
      ? [
          {
            name: '📊 ETF Portfolio',
            url: '/tools/etf-portfolio',
            description: 'Masterclass ETF basket & buy recs',
            badge: 'LIVE',
          },
        ]
      : []),
    ...(hasFeature('mf_evaluator_unlimited') || isAdmin
      ? [
          {
            name: '🧩 MF Evaluator',
            url: '/tools/mf-evaluator',
            description: 'Find the best mutual funds',
          },
        ]
      : []),
  ];

  const investPortfolio = [
    {
      name: '📊 Portfolio Tracker',
      url: '/tools/portfolio',
      description: 'Track all your investments',
    },
    {
      name: '🔬 Portfolio X-Ray',
      url: '/tools/portfolio/xray',
      description: "Find what's wrong with your money",
    },
    {
      name: '🏥 Portfolio Health Review',
      url: '/tools/portfolio/health-review',
      description: 'Deep analysis — sector, tax, debt quality',
      badge: 'NEW',
    },
    {
      name: '🧮 Investment Calculators',
      url: '/calculators',
      description: 'SIP, Lumpsum, CAGR & XIRR',
    },
  ];

  const learnCategories = [
    { name: 'Retirement Planning', url: '/learn/retirement-planning', icon: '🎯' },
    { name: 'Loans', url: '/learn/loans', icon: '🏠' },
    { name: 'Insurance', url: '/learn/insurance', icon: '🛡️' },
    { name: 'Tax Planning', url: '/learn/tax-planning', icon: '💰' },
    { name: 'Estate Planning', url: '/learn/estate-planning', icon: '📜' },
    { name: 'Investment Planning', url: '/learn/investment-planning', icon: '📈' },
  ];

  return (
    <>
      <nav className="main-navigation">
        <div className="nav-container">
          {/* Logo - Updated with SVG image */}
          <div className="nav-logo">
            <Link to="/" className="nav-logo-link">
              <img
                src="/logo.svg"
                alt="FINNCAL - Life on Your Terms"
                className="nav-logo-image"
                width={140}
                height={36}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="nav-menu desktop-menu">
            {/* Tools Dropdown (Plan + Explore + Retirement Research) */}
            <div className="nav-item dropdown" ref={toolsRef}>
              <button
                className="nav-link dropdown-toggle"
                onClick={() => setToolsDropdownOpen(!toolsDropdownOpen)}
                aria-expanded={toolsDropdownOpen}
                aria-haspopup="true"
              >
                Tools
                <span className="dropdown-arrow">▼</span>
              </button>

              {toolsDropdownOpen && (
                <div className="dropdown-menu tools-dropdown">
                  <div className="dropdown-header">PLAN YOUR FREEDOM</div>
                  {planItems.map((item) => (
                    <Link
                      key={item.url}
                      to={item.url}
                      className={`dropdown-item ${item.featured ? 'dropdown-item-featured' : ''}`}
                      onClick={() => setToolsDropdownOpen(false)}
                    >
                      <div className="dropdown-item-content">
                        <div className="dropdown-item-name">
                          {item.name}
                          {item.badge && (
                            <span
                              className="item-badge"
                              style={{ background: '#16a34a', color: 'white' }}
                            >
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <div className="dropdown-item-description">{item.description}</div>
                      </div>
                    </Link>
                  ))}

                  <div className="dropdown-divider"></div>
                  <div className="dropdown-header">EXPLORE</div>
                  {exploreItems.map((item) => (
                    <Link
                      key={item.url}
                      to={item.url}
                      className="dropdown-item"
                      onClick={() => setToolsDropdownOpen(false)}
                    >
                      <div className="dropdown-item-content">
                        <div className="dropdown-item-name">{item.name}</div>
                        <div className="dropdown-item-description">{item.description}</div>
                      </div>
                    </Link>
                  ))}

                  <div className="dropdown-divider"></div>
                  <div className="dropdown-header" style={{ fontSize: '0.65rem', opacity: 0.7 }}>
                    RETIREMENT IN INDIA: YOUR OPTIONS
                  </div>
                  {retirementTools.map((tool) => (
                    <Link
                      key={tool.url}
                      to={tool.url}
                      className="dropdown-item"
                      onClick={() => setToolsDropdownOpen(false)}
                      style={{ opacity: 0.85 }}
                    >
                      <div className="dropdown-item-content">
                        <div className="dropdown-item-name" style={{ fontSize: '0.85rem' }}>
                          {tool.name}
                        </div>
                        <div className="dropdown-item-description">{tool.description}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Invest Dropdown */}
            <div className="nav-item dropdown" ref={investRef}>
              <button
                className="nav-link dropdown-toggle"
                onClick={() => setInvestDropdownOpen(!investDropdownOpen)}
                aria-expanded={investDropdownOpen}
                aria-haspopup="true"
              >
                Invest
                <span className="dropdown-arrow">▼</span>
              </button>

              {investDropdownOpen && (
                <div className="dropdown-menu invest-dropdown-grid">
                  {/* Column 1: Signals & Picks */}
                  <div className="invest-dropdown-col">
                    <div className="dropdown-header">SIGNALS & PICKS</div>
                    {investSignals.map((item) => (
                      <Link
                        key={item.url}
                        to={item.url}
                        className="dropdown-item"
                        onClick={() => setInvestDropdownOpen(false)}
                      >
                        <div className="dropdown-item-content">
                          <div className="dropdown-item-name">
                            {item.name}
                            {item.badge && (
                              <span
                                className="item-badge"
                                style={{ background: '#16a34a', color: 'white' }}
                              >
                                {item.badge}
                              </span>
                            )}
                          </div>
                          <div className="dropdown-item-description">{item.description}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  {/* Column 2: My Portfolio */}
                  <div className="invest-dropdown-col">
                    <div className="dropdown-header">MY PORTFOLIO</div>
                    {investPortfolio.map((item) => (
                      <Link
                        key={item.url}
                        to={item.url}
                        className="dropdown-item"
                        onClick={() => setInvestDropdownOpen(false)}
                      >
                        <div className="dropdown-item-content">
                          <div className="dropdown-item-name">
                            {item.name}
                            {item.badge && (
                              <span
                                className="item-badge"
                                style={{ background: '#16a34a', color: 'white' }}
                              >
                                {item.badge}
                              </span>
                            )}
                          </div>
                          <div className="dropdown-item-description">{item.description}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Learn Dropdown */}
            <div className="nav-item dropdown" ref={learnRef}>
              <button
                className="nav-link dropdown-toggle"
                onClick={() => setLearnDropdownOpen(!learnDropdownOpen)}
                aria-expanded={learnDropdownOpen}
                aria-haspopup="true"
              >
                Learn
                <span className="dropdown-arrow">▼</span>
              </button>

              {learnDropdownOpen && (
                <div className="dropdown-menu learn-dropdown">
                  <div className="dropdown-header">TOPICS</div>
                  {learnCategories.map((category) => (
                    <Link
                      key={category.url}
                      to={category.url}
                      className="dropdown-item"
                      onClick={() => setLearnDropdownOpen(false)}
                    >
                      <span className="dropdown-item-icon">{category.icon}</span>
                      <span className="dropdown-item-name">{category.name}</span>
                    </Link>
                  ))}
                  <div className="dropdown-divider"></div>
                  <Link
                    to="/learn"
                    className="dropdown-item view-all"
                    onClick={() => setLearnDropdownOpen(false)}
                  >
                    View All Articles →
                  </Link>
                </div>
              )}
            </div>

            {/* Tax - Top-level link */}
            <div className="nav-item">
              <Link to="/tools/tax-freedom" className="nav-link" style={{ position: 'relative' }}>
                Tax
                <span
                  className="item-badge"
                  style={{
                    background: '#16a34a',
                    color: 'white',
                    position: 'absolute',
                    top: '-4px',
                    right: '-28px',
                    fontSize: '0.55rem',
                    padding: '1px 4px',
                  }}
                >
                  NEW
                </span>
              </Link>
            </div>

            {/* City Finder - Admin only */}
            {isAdmin && (
              <div className="nav-item">
                <Link to="/city-finder" className="nav-link">
                  City Finder
                </Link>
              </div>
            )}

            {/* Pricing */}
            <div className="nav-item">
              <Link to="/pricing" className="nav-link">
                Subscription
              </Link>
            </div>

            {/* About */}
            <div className="nav-item">
              <Link to="/about" className="nav-link">
                About
              </Link>
            </div>

            {/* Contact */}
            <div className="nav-item">
              <Link to="/contact" className="nav-link">
                Contact
              </Link>
            </div>

            {/* User Menu / Auth */}
            {user ? (
              <div className="nav-item dropdown user-menu" ref={userMenuRef}>
                <button
                  className="nav-link dropdown-toggle user-toggle"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <span className="user-name">
                    {user.user_metadata?.full_name?.split(' ')[0] ||
                      user.email?.split('@')[0] ||
                      'User'}
                  </span>
                  <span className="dropdown-arrow">▼</span>
                </button>

                {userMenuOpen && (
                  <div className="dropdown-menu user-dropdown">
                    <div className="dropdown-header user-email">{user.email}</div>
                    <div
                      className="dropdown-item"
                      style={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '6px',
                        cursor: 'default',
                      }}
                    >
                      <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>
                        📱 Mobile Number
                      </span>
                      <div style={{ display: 'flex', gap: '6px', width: '100%' }}>
                        <input
                          type="tel"
                          value={mobile}
                          onChange={(e) => {
                            setMobile(e.target.value);
                            setMobileSaved(false);
                          }}
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveMobile()}
                          placeholder="10-digit mobile"
                          maxLength={10}
                          style={{
                            flex: 1,
                            padding: '5px 8px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '13px',
                            minWidth: 0,
                          }}
                        />
                        <button
                          onClick={handleSaveMobile}
                          disabled={mobileSaving}
                          style={{
                            padding: '5px 10px',
                            background: mobileSaved ? '#16a34a' : '#2563eb',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {mobileSaving ? '…' : mobileSaved ? 'Saved!' : 'Save'}
                        </button>
                      </div>
                    </div>
                    <Link
                      to="/pricing"
                      className="dropdown-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      ⭐ My Subscription
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="dropdown-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        ⚙️ Admin Panel
                      </Link>
                    )}
                    <div className="dropdown-divider"></div>
                    <button
                      onClick={() => {
                        setShowDeleteModal(true);
                        setUserMenuOpen(false);
                      }}
                      className="dropdown-item delete-data-item"
                      style={{ color: '#dc2626' }}
                    >
                      🗑️ Delete My Data
                    </button>
                    <div className="dropdown-divider"></div>
                    <button onClick={handleSignOut} className="dropdown-item sign-out">
                      🚪 Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="nav-item">
                <button
                  className="nav-link auth-button"
                  onClick={() => {
                    localStorage.setItem(
                      'finncal_preAuthState',
                      JSON.stringify({
                        returnPath: window.location.pathname,
                        pendingSave: false,
                        timestamp: Date.now(),
                      })
                    );
                    setShowAuthModal(true);
                  }}
                >
                  Sign In
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            {/* Plan Your Freedom Section */}
            <div className="mobile-menu-section">
              <div className="mobile-menu-header">PLAN YOUR FREEDOM</div>
              {planItems.map((item) => (
                <Link
                  key={item.url}
                  to={item.url}
                  className={`mobile-menu-item ${item.featured ? 'mobile-menu-item-featured' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="mobile-item-name">
                    {item.name}
                    {item.badge && (
                      <span
                        className="mobile-item-badge"
                        style={{ background: '#16a34a', color: 'white' }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <div className="mobile-item-description">{item.description}</div>
                </Link>
              ))}
            </div>

            {/* Explore Section */}
            <div className="mobile-menu-section">
              <div className="mobile-menu-header">EXPLORE</div>
              {exploreItems.map((item) => (
                <Link
                  key={item.url}
                  to={item.url}
                  className="mobile-menu-item"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="mobile-item-name">{item.name}</div>
                  <div className="mobile-item-description">{item.description}</div>
                </Link>
              ))}
            </div>

            {/* Retirement Research Tools */}
            <div className="mobile-menu-section">
              <div className="mobile-menu-header" style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                RETIREMENT IN INDIA: YOUR OPTIONS
              </div>
              {retirementTools.map((tool) => (
                <Link
                  key={tool.url}
                  to={tool.url}
                  className="mobile-menu-item"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ opacity: 0.85 }}
                >
                  <div className="mobile-item-name" style={{ fontSize: '0.9rem' }}>
                    {tool.name}
                  </div>
                  <div className="mobile-item-description">{tool.description}</div>
                </Link>
              ))}
            </div>

            {/* Invest Section */}
            <div className="mobile-menu-section">
              <div className="mobile-menu-header">SIGNALS & PICKS</div>
              {investSignals.map((item) => (
                <Link
                  key={item.url}
                  to={item.url}
                  className="mobile-menu-item"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="mobile-item-name">
                    {item.name}
                    {item.badge && (
                      <span
                        className="mobile-item-badge"
                        style={{ background: '#16a34a', color: 'white' }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <div className="mobile-item-description">{item.description}</div>
                </Link>
              ))}
            </div>
            <div className="mobile-menu-section">
              <div className="mobile-menu-header">MY PORTFOLIO</div>
              {investPortfolio.map((item) => (
                <Link
                  key={item.url}
                  to={item.url}
                  className="mobile-menu-item"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="mobile-item-name">
                    {item.name}
                    {item.badge && (
                      <span
                        className="mobile-item-badge"
                        style={{ background: '#16a34a', color: 'white' }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <div className="mobile-item-description">{item.description}</div>
                </Link>
              ))}
            </div>

            {/* Learn Section */}
            <div className="mobile-menu-section">
              <div className="mobile-menu-header">LEARN</div>
              {learnCategories.map((category) => (
                <Link
                  key={category.url}
                  to={category.url}
                  className="mobile-menu-item"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mobile-item-icon">{category.icon}</span>
                  <span className="mobile-item-name">{category.name}</span>
                </Link>
              ))}
              <Link
                to="/learn"
                className="mobile-menu-item view-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                View All Articles →
              </Link>
            </div>

            {/* Tax Section */}
            <div className="mobile-menu-section">
              <div className="mobile-menu-header">TAX</div>
              <Link
                to="/tools/tax-freedom"
                className="mobile-menu-item"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="mobile-item-name">
                  Tax Freedom
                  <span
                    className="mobile-item-badge"
                    style={{ background: '#16a34a', color: 'white' }}
                  >
                    NEW
                  </span>
                </div>
                <div className="mobile-item-description">Validate your ITR for free</div>
              </Link>
            </div>

            {/* Other Links */}
            <div className="mobile-menu-section">
              {isAdmin && (
                <Link
                  to="/city-finder"
                  className="mobile-menu-item"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="mobile-item-name">City Finder</div>
                  <div className="mobile-item-description">Compare 62 Indian cities</div>
                </Link>
              )}
              <Link
                to="/pricing"
                className="mobile-menu-item"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="mobile-item-name">Subscription</div>
                <div className="mobile-item-description">Free &amp; premium plans</div>
              </Link>
              <Link
                to="/about"
                className="mobile-menu-item"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="mobile-item-name">About</div>
              </Link>
              <Link
                to="/contact"
                className="mobile-menu-item"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="mobile-item-name">Contact</div>
              </Link>
            </div>

            {/* User Section */}
            <div className="mobile-menu-section">
              {user ? (
                <>
                  <div className="mobile-user-info">
                    <span className="mobile-user-name" style={{ fontWeight: '600' }}>
                      {user.user_metadata?.full_name?.split(' ')[0] ||
                        user.email?.split('@')[0] ||
                        'User'}
                    </span>
                    <span className="mobile-user-email" style={{ marginLeft: '8px', opacity: 0.7 }}>
                      {user.email}
                    </span>
                  </div>
                  <div
                    className="mobile-menu-item"
                    style={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '6px',
                      cursor: 'default',
                    }}
                  >
                    <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>
                      📱 Mobile Number
                    </span>
                    <div style={{ display: 'flex', gap: '6px', width: '100%' }}>
                      <input
                        type="tel"
                        value={mobile}
                        onChange={(e) => {
                          setMobile(e.target.value);
                          setMobileSaved(false);
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveMobile()}
                        placeholder="10-digit mobile"
                        maxLength={10}
                        style={{
                          flex: 1,
                          padding: '6px 10px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '14px',
                          minWidth: 0,
                        }}
                      />
                      <button
                        onClick={handleSaveMobile}
                        disabled={mobileSaving}
                        style={{
                          padding: '6px 12px',
                          background: mobileSaved ? '#16a34a' : '#2563eb',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {mobileSaving ? '…' : mobileSaved ? 'Saved!' : 'Save'}
                      </button>
                    </div>
                  </div>
                  <Link
                    to="/pricing"
                    className="mobile-menu-item"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="mobile-item-name">⭐ My Subscription</div>
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="mobile-menu-item"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="mobile-item-name">⚙️ Admin Panel</div>
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      setShowDeleteModal(true);
                      setMobileMenuOpen(false);
                    }}
                    className="mobile-menu-item delete-data-mobile"
                    style={{ color: '#dc2626' }}
                  >
                    <div className="mobile-item-name">🗑️ Delete My Data</div>
                  </button>
                  <button
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                    className="mobile-menu-item sign-out-button"
                  >
                    <div className="mobile-item-name">🚪 Sign Out</div>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    localStorage.setItem(
                      'finncal_preAuthState',
                      JSON.stringify({
                        returnPath: window.location.pathname,
                        pendingSave: false,
                        timestamp: Date.now(),
                      })
                    );
                    setShowAuthModal(true);
                    setMobileMenuOpen(false);
                  }}
                  className="mobile-menu-item auth-button-mobile"
                >
                  <div className="mobile-item-name">🔐 Sign In</div>
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Delete Data Modal */}
      {showDeleteModal && (
        <DeleteDataModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} />
      )}
    </>
  );
};

export default Navigation;
